import type {NavigationMenuType} from '../types/navigation-menu.type'
import {normalizeNavigationRouteKey} from './navigation-route.utils'

export type PermissionMatchMode = 'all' | 'any'

export type NavigationMenuInjectionItemTemplate = {
  id: string
  key: string
  label: string
  route: string
  segment?: string | null
  labelB2c?: string | null
  icon?: string | null
}

export type NavigationMenuInjectionSiblingMatcher = {
  includes: string
  excludes?: string
}

export type NavigationMenuInjectionRule = {
  item: NavigationMenuInjectionItemTemplate
  shouldInjectWhenSiblings: (children: NavigationMenuType[]) => boolean
}

export function createRouteSegmentMatcher(segment: string): (normalizedRoute: string) => boolean {
  return (normalizedRoute) => normalizedRoute.includes(segment)
}

export function createSiblingRouteMatcher(matcher: NavigationMenuInjectionSiblingMatcher): (children: NavigationMenuType[]) => boolean {
  return (children) =>
    children.some((child) => {
      const route = normalizeNavigationRouteKey(child.route)
      if (!route.includes(matcher.includes)) return false
      if (matcher.excludes && route.includes(matcher.excludes)) return false
      return true
    })
}

function buildInjectedNavigationMenuItem(template: NavigationMenuInjectionItemTemplate, parentId: string, orderIndex: number): NavigationMenuType {
  const route = template.route.trim()
  const normalizedRoute = route.startsWith('/') ? route : `/${route}`

  return {
    id: template.id,
    key: template.key,
    parentId,
    label: template.label,
    labelB2c: template.labelB2c ?? null,
    route: normalizedRoute,
    icon: template.icon ?? null,
    orderIndex,
    segment: template.segment ?? null,
    children: [],
  }
}

export type NavigationMenuVisibilityRule = {
  matchesRoute: (normalizedRoute: string) => boolean
  permissions: readonly string[]
  permissionMatch?: PermissionMatchMode
  hideForB2c?: boolean
  injection?: NavigationMenuInjectionRule
}

export type NavigationMenuVisibilityOptions = {
  userIsB2c?: boolean
}

export function userHasMenuPermissions(
  userPermissions: readonly string[],
  requiredPermissions: readonly string[],
  permissionMatch: PermissionMatchMode = 'any',
): boolean {
  if (requiredPermissions.length === 0) return true
  return permissionMatch === 'any'
    ? requiredPermissions.some((permission) => userPermissions.includes(permission))
    : requiredPermissions.every((permission) => userPermissions.includes(permission))
}

function findMatchingRule(route: string | null | undefined, rules: readonly NavigationMenuVisibilityRule[]): NavigationMenuVisibilityRule | undefined {
  const normalizedRoute = normalizeNavigationRouteKey(route)
  if (!normalizedRoute) return undefined
  return rules.find((rule) => rule.matchesRoute(normalizedRoute))
}

function canSeeByRule(rule: NavigationMenuVisibilityRule, userPermissions: readonly string[], userIsB2c: boolean): boolean {
  if (userIsB2c && rule.hideForB2c) return false
  return userHasMenuPermissions(userPermissions, rule.permissions, rule.permissionMatch ?? 'any')
}

function applyInjection(children: NavigationMenuType[], item: NavigationMenuType, rule: NavigationMenuVisibilityRule): NavigationMenuType[] {
  const injection = rule.injection
  if (!injection) return children

  const alreadyPresent = children.some((child) => rule.matchesRoute(normalizeNavigationRouteKey(child.route)))
  if (alreadyPresent) return children

  if (!injection.shouldInjectWhenSiblings(children)) return children

  const nextOrder = children.reduce((max, child) => Math.max(max, child.orderIndex ?? 0), 0) + 1
  const injected = buildInjectedNavigationMenuItem(injection.item, item.id, nextOrder)
  return [...children, injected].sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
}

export function applyNavigationMenuVisibility(
  items: NavigationMenuType[],
  userPermissions: readonly string[],
  rules: readonly NavigationMenuVisibilityRule[],
  options?: NavigationMenuVisibilityOptions,
): NavigationMenuType[] {
  const userIsB2c = options?.userIsB2c ?? false

  const visit = (item: NavigationMenuType): NavigationMenuType => {
    let children = (item.children ?? []).map(visit)

    children = children.filter((child) => {
      const rule = findMatchingRule(child.route, rules)
      if (!rule) return true
      return canSeeByRule(rule, userPermissions, userIsB2c)
    })

    for (const rule of rules) {
      if (!canSeeByRule(rule, userPermissions, userIsB2c)) continue
      children = applyInjection(children, item, rule)
    }

    return {...item, children}
  }

  return items.map(visit)
}
